"use client";

import { useQuery } from "@tanstack/react-query";

export interface SmartHolder {
  address: string;
  side: "YES" | "NO";
  shares: number;
  primary_style: string;
}

export interface SmartMoneyData {
  net_positioning: number;
  smart_wallets_yes: number;
  smart_wallets_no: number;
  total_smart_volume: number;
  top_holders?: SmartHolder[];
}

async function fetchSmartMoney(conditionId: string): Promise<SmartMoneyData | null> {
  const res = await fetch(`/api/analytics/market/${conditionId}/smart`);
  if (!res.ok) return null;
  const json = await res.json();
  if (!json?.success || !json.data) return null;
  return json.data as SmartMoneyData;
}

/**
 * Shared hook so every component rendering the same conditionId reuses one
 * in-flight request + cached response. Predexon-only data, so callers should
 * gate by `enabled` (e.g. `source === 'polymarket' && !!conditionId`).
 */
export function useSmartMoney(conditionId: string | undefined | null, enabled = true) {
  return useQuery<SmartMoneyData | null>({
    queryKey: ["analytics", "smart-money", conditionId],
    queryFn: () => fetchSmartMoney(conditionId as string),
    // Skip under HIDE_PREDEXON — the market smart-money endpoint is down during
    // the Predexon outage, so consumers hide instead of showing empty data.
    enabled: enabled && !!conditionId && process.env.NEXT_PUBLIC_HIDE_PREDEXON_TABS !== "true",
    staleTime: 2 * 60 * 1000, // 2 min
    gcTime: 10 * 60 * 1000,
    retry: false,
  });
}
